import React, { useState } from 'react'
import { Question } from '@mission.io/question-views'
import {
	getCorrectAnswerForQuestion,
	type ClientQuestion,
	type StudentAnswer,
	type ClassScore,
} from '@mission.io/question-toolkit'
import { HexagonOverlay } from './HexagonOverlay'
import type { QuestionScoreMap, Score } from './types'
import QuestionModal from './QuestionModal'
import { StyledQuestionCard, StyledQuestionGrid } from './SharedComponents'

/**
 * QuestionGrid - show a grid of questions with the correct answers highlighted and the scores provided
 *
 * @param {Object} props - the react props
 * @param  {ClientQuestion[]} props.questions - the questions to display
 * @param  {QuestionScoreMap} props.questionScores - the score of the questions
 *
 * @returns React$Node
 */
export function QuestionGrid({
	questions,
	questionScores,
	questionClassScores,
}: {
	questions: ClientQuestion[],
	questionScores: QuestionScoreMap,
	questionClassScores: { [questionId: string]: ClassScore },
}): React$Node {
	const [modalQuestionId, setModalQuestionId] = useState(null)
	const correctQuestionAnswers: { [questionId: string]: StudentAnswer } = {}

	let modalQuestion: ?ClientQuestion = null

	const questionIdToIndex: { [questionId: string]: number } = {}

	questions.forEach((question, index) => {
		if (question._id === modalQuestionId) {
			modalQuestion = question
		}

		questionIdToIndex[question._id] = index

		const correctAnswer = getCorrectAnswerForQuestion(question)

		if (!correctAnswer) {
			return
		}

		correctQuestionAnswers[question._id] = correctAnswer
	})

	const modalQuestionScores: ?Score = modalQuestion
		? questionScores[modalQuestion._id]
		: null
	const modalClassQuestionScores: ?ClassScore = modalQuestionId
		? questionClassScores[modalQuestionId]
		: null

	return (
		<>
			<StyledQuestionGrid>
				{questions.map((question: ClientQuestion, index: number) => (
					<StyledQuestionCard
						key={index}
						onClick={() => {
							setModalQuestionId(question._id)
						}}>
						<Question
							question={question}
							review="OVERVIEW"
							studentResponse={{
								answer: correctQuestionAnswers[question._id],
								score: null,
							}}
							questionIndex={index}
						/>
						<HexagonOverlay score={questionScores[question._id]} />
					</StyledQuestionCard>
				))}
			</StyledQuestionGrid>
			{modalQuestionScores && modalQuestion && modalClassQuestionScores ? (
				<QuestionModal
					question={modalQuestion}
					score={modalQuestionScores}
					classScore={modalClassQuestionScores}
					onDismiss={() => setModalQuestionId(null)}
					questionNumber={
						((modalQuestionId && questionIdToIndex[modalQuestionId]) || 0) + 1
					}
				/>
			) : null}
		</>
	)
}
